import { CommonModule } from "@angular/common";
import { Component, effect, forwardRef, Input, isDevMode, OnChanges, signal, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { InputTextModule } from "primeng/inputtext";
import { SelectButtonModule } from 'primeng/selectbutton';

export type CustomFieldNifVariant = 'DNI_ONLY' | 'DNI_NIE_PASSPORT';
type CustomFieldNifDocumentType = 'DNI_NIE' | 'PASSPORT';

@Component({
  selector: 'app-custom-field-nif',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomFieldNif),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomFieldNif),
      multi: true
    }
  ],
  templateUrl: './nif.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectButtonModule,
],
  styleUrls: ['../../styles/fields.scss']
})
export class CustomFieldNif implements ControlValueAccessor, Validator, OnChanges {

  @Input()
  public readonly: boolean;

  /** DNI_ONLY: solo DNI/NIE con letra. DNI_NIE_PASSPORT: DNI/NIE o pasaporte (alfanumérico permisivo). */
  @Input()
  public variant: CustomFieldNifVariant = 'DNI_ONLY';

  /** Selector UI (solo visible en variant DNI_NIE_PASSPORT). */
  public readonly documentType = signal<CustomFieldNifDocumentType>('DNI_NIE');

  public readonly documentTypeOptions: Array<{ label: string; value: CustomFieldNifDocumentType }> = [
    { label: 'DNI/NIE', value: 'DNI_NIE' },
    { label: 'Passaport', value: 'PASSPORT' },
  ];

  public internalValue: string;

  onChange: (_: string) => void = (_: string) => { };
  private onValidatorChange: () => void = () => {};
  private readonly debugEnabled = isDevMode();

  constructor() {
    effect(() => {
      const docType = this.documentType();
      if (this.debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug('[CustomFieldNif] documentType changed', { variant: this.variant, documentType: docType, internalValue: this.internalValue });
      }
      this.onValidatorChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['variant']?.currentValue !== 'DNI_NIE_PASSPORT') {
      this.documentType.set('DNI_NIE');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  writeValue(val: string): void {
    this.internalValue = val;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const rawValue = (control?.value ?? '') as string;
    const value = rawValue?.replace(/\s+/g, '').toUpperCase();

    if (value) {
      if (this.variant === 'DNI_NIE_PASSPORT') {
        const ok = this.documentoIdentidadValido(value);
        if (this.debugEnabled) {
          // eslint-disable-next-line no-console
          console.debug('[CustomFieldNif] validate', { variant: this.variant, documentType: this.documentType(), rawValue, normalizedValue: value, ok });
        }
        if (!ok) {
          return { format: true };
        }
      } else {
        const ok = this.nifValido(value);
        if (this.debugEnabled) {
          // eslint-disable-next-line no-console
          console.debug('[CustomFieldNif] validate', { variant: this.variant, rawValue, normalizedValue: value, ok });
        }
        if (!ok) {
          return { format: true };
        }
      }
    }

    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  updateChanges(value: string) {
    this.internalValue = value;
    this.onChange(value);
  }

  public nifValido(nif: string): boolean {
    nif = nif.toUpperCase();

    const nifRegEx = /^[0-9]{8}[A-Z]$/i;
    const nieRegEx = /^[XYZ][0-9]{7}[A-Z]$/i;
    const letras = "TRWAGMYFPDXBNJZSQVHLCKE";

    if (nifRegEx.test(nif)) {
      return letras[parseInt(nif.substring(0, 8)) % 23] === nif[8];
    } else if (nieRegEx.test(nif)) {
      const firstChar = nif[0];
      if (firstChar === "X") nif = "0" + nif.substring(1);
      else if (firstChar === "Y") nif = "1" + nif.substring(1);
      else if (firstChar === "Z") nif = "2" + nif.substring(1);

      return letras[parseInt(nif.substring(0, 8)) % 23] === nif[8];
    } else {
      return false;
    }
  }

  /**
   * DNI/NIE con letra válidos, o pasaporte (hábito ES: 6–9 alfanuméricos, p. ej. PA1234567).
   * En variant DNI_NIE_PASSPORT el usuario elige el tipo y la validación es estricta.
   */
  public documentoIdentidadValido(raw: string): boolean {
    const normalized = raw.replace(/\s+/g, '').toUpperCase();
    if (!normalized) return false;

    if (this.documentType() === 'PASSPORT') {
      return this.passportValido(normalized);
    }

    return this.nifValido(normalized);
  }

  private passportValido(normalized: string): boolean {
    // Importante: un DNI/NIE NO puede ser válido como pasaporte al cambiar el botón.
    // UE (habitual): 1–3 letras + 5–9 dígitos (p. ej. PA1234567)
    const passportEu = /^[A-Z]{1,3}[0-9]{5,9}$/;

    // Español (observado): 3 letras + 6 dígitos + letra final (p. ej. DND636952T)
    const passportEs = /^[A-Z]{3}[0-9]{6}[A-Z]$/;

    // Internacional (genérico): 6–9 alfanuméricos, empieza por letra y contiene dígitos.
    // Además excluye explícitamente patrones de DNI/NIE para evitar falsos positivos (p. ej. 12345678A).
    const passportIntl = /^(?![0-9]{8}[A-Z]$)(?![XYZ][0-9]{7}[A-Z]$)(?=.*\d)[A-Z][A-Z0-9]{5,8}$/;

    return passportEu.test(normalized) || passportEs.test(normalized) || passportIntl.test(normalized);
  }
}
